import { PluginObject } from "vue"
import { Store } from "vuex"

export interface Role {
    name: string
    value: string
    priority: number
}

export const Roles = {
    ROLE_USER: { name: "User", value: "ROLE_USER", priority: 0 },
    ROLE_USER_BASIC: {
        name: "Basic User",
        value: "ROLE_USER_BASIC",
        priority: 1,
    },
    ROLE_USER_ADVANCED: {
        name: "Advanced User",
        value: "ROLE_USER_ADVANCED",
        priority: 2,
    },
    ROLE_AGGREGATOR: {
        name: "Aggregator",
        value: "ROLE_AGGREGATOR",
        priority: 5,
    },
    ROLE_SUPER_ADMIN: {
        name: "Super Admin",
        value: "ROLE_SUPER_ADMIN",
        priority: 10,
    },
}

type Permission = {
    id: number
    type: "location" | "group"
    view: boolean
    edit: boolean
    control: boolean
}

type UserPermissions = {
    location: Permission[]
    group: Permission[]
}

export interface VuePermissionsPlugin {
    can(ability: "view" | "edit" | "control", subject: "location" | "group", id: number): boolean

    hasRole(role: Role): boolean
}

export interface UserController {
    alarmEmailOptIn: boolean
    alarmOkEmailOptIn: boolean
    alarmPushNotificationOptIn: boolean
    alarmOkPushNotificationOptIn: boolean
    id: number
    email: string
    roles: string[]
    firstName: string
    lastName: string
    fullName: string
    locale: string
    role: string
    deletedAt: string | null
    city: string | null
    postcode: string | null
    street: string | null
    houseNumber: number | null
    defaultLocationView: string | null
}

export interface User {
    id: number
    firstName: string
    lastName: string
    fullName: string
    role: string
    roles: string[]
    locations: Object[]
    controllers: UserController[]
    permissions: Object[]
}

export class PermissionsService implements VuePermissionsPlugin {
    private availableRoles: Role[]

    private store: Store<any>

    public constructor(store: Store<any>) {
        this.store = store

        this.availableRoles = Object.values(Roles)
    }

    permissionsLoaded(): boolean {
        return this.store.getters["auth/permissionsLoaded"]
    }

    get user(): User | null {
        return this.store.getters["auth/user"]
    }

    getAssignableRoles() {
        return [Roles.ROLE_USER, Roles.ROLE_USER_BASIC, Roles.ROLE_USER_ADVANCED]
    }

    canAccessAnyLocation() {
        if (!this.user) {
            return false
        }

        if (this.user.locations.length > 0) {
            return true
        }

        const locationRules = this.permissions?.location

        return this.hasSuperAdminRole() || (locationRules !== undefined && locationRules.length > 0)
    }

    can(ability: "view" | "edit" | "control", subject: "location" | "group", id: number): boolean {
        if (this.hasAggregatorRole()) {
            return true
        }

        if (!this.permissions) {
            return false
        }

        const rule = this.permissions?.[subject].find((permission) => permission.id == id)

        if (!rule) {
            return false
        }

        return rule[ability]
    }

    canManageRole(role: Role) {
        const userRole = this.getActiveRole()

        if (
            role.value === Roles.ROLE_SUPER_ADMIN.value &&
            userRole.value !== Roles.ROLE_SUPER_ADMIN.value
        ) {
            return false
        }

        return userRole.priority >= role.priority
    }

    hasRole(role: Role): boolean {
        return this.getUserRoles().find((userRole) => userRole.value === role.value) !== undefined
    }

    tryParseRoleOrDefault(roleString: string): Role {
        if (roleString.length === 0) {
            return Roles.ROLE_USER
        }

        const role = this.availableRoles.find((role) => role.value === roleString)

        return role ? role : Roles.ROLE_USER
    }

    getActiveRole(): Role {
        if (!this.user) {
            return Roles.ROLE_USER
        }

        const role = this.getUserRoles().find((role) => role !== undefined)

        return role ? role : Roles.ROLE_USER
    }

    hasBasicUserRole(): boolean {
        return this.hasAdvancedUserRole() || this.hasRole(Roles.ROLE_USER_BASIC)
    }

    hasAdvancedUserRole(): boolean {
        return this.hasAggregatorRole() || this.hasRole(Roles.ROLE_USER_ADVANCED)
    }

    hasAggregatorRole(): boolean {
        return this.hasSuperAdminRole() || this.hasRole(Roles.ROLE_AGGREGATOR)
    }

    hasSuperAdminRole(): boolean {
        return this.hasRole(Roles.ROLE_SUPER_ADMIN)
    }

    get permissions(): UserPermissions | null {
        return this.store.getters["auth/permissions"]
    }

    getUserRoles(): Role[] {
        if (!this.user) {
            return []
        }

        const roles: Role[] = []

        type RoleKey = keyof typeof Roles
        this.user.roles.forEach((role) => {
            const roleKey = role as RoleKey

            if (Object.prototype.hasOwnProperty.call(Roles, roleKey)) {
                roles.push(Roles[roleKey])
            }
        })

        return roles
    }

    getIsControllerOwnedByUser(controllerId: number): boolean {
        if (!this.user) {
            return false
        }

        return this.user.controllers.some((controller) => controller.id === controllerId)
    }
}

const PermissionsPlugin: PluginObject<any> = {
    install: (Vue, options?: { store: Store<any> }) => {
        if (!options) {
            return
        }

        Vue.prototype.$permissions = new PermissionsService(options.store)
    },
}

export default PermissionsPlugin
